import React, { useCallback, useEffect, useState } from "react";
import { listarDiplomasPromocion } from "../../../../services/promocionesService";
import {
  generarMencionHonorPorMatricula,
  descargarMencionHonorPorMatricula,
  descargarMencionesHonorBatch
} from "../../../../services/mencionesHonorService";
import { showToast } from "../../../../utils/showToast";

const buildNombre = (row) => {
  const nombre = [row.apellidos, row.nombres].filter(Boolean).join(" ").trim();
  return nombre || `Estudiante #${row.estudiante_id || "-"}`;
};

const sortByApellidoNombre = (list = []) =>
  [...list].sort((a, b) => {
    const apellidosA = String(a?.apellidos || "").trim();
    const apellidosB = String(b?.apellidos || "").trim();
    const apellidoCompare = apellidosA.localeCompare(apellidosB, "es", { sensitivity: "base" });
    if (apellidoCompare !== 0) return apellidoCompare;
    const nombresA = String(a?.nombres || "").trim();
    const nombresB = String(b?.nombres || "").trim();
    return nombresA.localeCompare(nombresB, "es", { sensitivity: "base" });
  });

const getMatriculaId = (row) => row?.matricula_id || row?.matriculaId;

export default function PromocionMencionesTab({ promocionId }) {
  const [aprobados, setAprobados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatingGeneral, setGeneratingGeneral] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadAprobados = useCallback(async () => {
    if (!promocionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listarDiplomasPromocion(promocionId);
      setAprobados(sortByApellidoNombre(Array.isArray(data) ? data : []));
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar los aprobados.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [promocionId]);

  useEffect(() => {
    loadAprobados();
  }, [loadAprobados]);

  const handleGenerarGeneral = useCallback(async () => {
    if (!promocionId || !aprobados.length) return;
    const matriculaIds = aprobados
      .filter((row) => row.elegible_mencion_honor !== false)
      .map(getMatriculaId)
      .filter(Boolean);
    if (!matriculaIds.length) {
      showToast({ type: "warning", message: "No se encontraron matriculas elegibles para generar menciones." });
      return;
    }
    setGeneratingGeneral(true);
    try {
      await descargarMencionesHonorBatch(matriculaIds, "TWO_UP", promocionId);
      showToast({ type: "success", message: "Menciones generadas y descargadas correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudieron generar las menciones." });
    } finally {
      setGeneratingGeneral(false);
    }
  }, [promocionId, aprobados]);

  const handleGenerarIndividual = useCallback(async (row) => {
    const matriculaId = getMatriculaId(row);
    if (!matriculaId) {
      showToast({ type: "warning", message: "No se encontro la matricula para este estudiante." });
      return;
    }
    setGeneratingId(matriculaId);
    try {
      await generarMencionHonorPorMatricula(matriculaId, "SINGLE");
      await descargarMencionHonorPorMatricula(matriculaId);
      showToast({ type: "success", message: `Mencion generada y descargada para ${buildNombre(row)}.` });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo generar la mencion." });
    } finally {
      setGeneratingId(null);
    }
  }, []);

  const handleDescargarIndividual = useCallback(async (row) => {
    const matriculaId = getMatriculaId(row);
    if (!matriculaId) {
      showToast({ type: "warning", message: "No se encontro la matricula para este estudiante." });
      return;
    }
    setDownloadingId(matriculaId);
    try {
      await descargarMencionHonorPorMatricula(matriculaId);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo descargar la mencion." });
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const total = aprobados.length;
  const emptyRows = Math.max(0, 10 - total);

  return (
    <div className="management-card promociones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Menciones de honor</p>
          <h5 className="mb-0">Estudiantes aprobados</h5>
          <small className="text-muted">Total aprobados: {total}</small>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadAprobados} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={handleGenerarGeneral}
            disabled={loading || generatingGeneral || !total}
          >
            {generatingGeneral ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            ) : (
              <i className="bi bi-award me-2" />
            )}
            {generatingGeneral ? "Generando..." : "Generar menciones"}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-4">
          <div className="me-3">Menciones: {error}</div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadAprobados}>
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <div className="management-table-wrapper">
          <table className="table align-middle management-table promociones-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Matricula</th>
                <th>Estudiante</th>
                <th>Documento</th>
                <th>Nota curso</th>
                <th>Resultado</th>
                <th>Mencion honor</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {aprobados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-3">
                    No hay estudiantes aprobados para esta promocion.
                  </td>
                </tr>
              ) : (
                aprobados.map((row, idx) => {
                  const matriculaId = getMatriculaId(row);
                  const isGenerating = generatingId && matriculaId === generatingId;
                  const isDownloading = downloadingId && matriculaId === downloadingId;
                  const noElegible = row.elegible_mencion_honor === false;
                  return (
                    <tr key={row.estudiante_formacion_id || row.matricula_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{row.codigo_matricula || "-"}</td>
                      <td className="fw-semibold">{buildNombre(row)}</td>
                      <td>{row.documento_identidad || "-"}</td>
                      <td>{row.nota_curso ?? "-"}</td>
                      <td>
                        <span className="badge rounded-pill px-3 py-2 bg-success text-white">
                          Aprobado
                        </span>
                      </td>
                      <td>
                        {noElegible ? (
                          <span
                            className="badge rounded-pill px-3 py-2 bg-danger-subtle text-danger"
                            title={row.motivo_no_elegible}
                          >
                            No elegible — {row.motivo_no_elegible}
                          </span>
                        ) : (
                          <span className="badge rounded-pill px-3 py-2 bg-success-subtle text-success">
                            Elegible
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleGenerarIndividual(row)}
                            disabled={generatingGeneral || isGenerating || isDownloading || !matriculaId || noElegible}
                            aria-label={`Generar mencion de ${buildNombre(row)}`}
                            title={noElegible ? row.motivo_no_elegible : "Generar mencion individual"}
                          >
                            {isGenerating ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            ) : (
                              <i className="bi bi-award" />
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleDescargarIndividual(row)}
                            disabled={generatingGeneral || isGenerating || isDownloading || !matriculaId || noElegible}
                            aria-label={`Descargar mencion de ${buildNombre(row)}`}
                            title={noElegible ? row.motivo_no_elegible : "Descargar mencion"}
                          >
                            {isDownloading ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            ) : (
                              <i className="bi bi-download" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {aprobados.length > 0 &&
                emptyRows > 0 &&
                Array.from({ length: emptyRows }).map((_, idx) => (
                  <tr key={`aprobados-empty-${idx}`} className="promociones-table-empty-row" aria-hidden="true">
                    <td colSpan={8}>&nbsp;</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert alert-info mt-3 mb-0">
        Las menciones generadas se guardan en el sistema y quedan listas para descarga.
      </div>
    </div>
  );
}
